import type { TableResponseBody } from '@ipfabric/utils';
import type { MandatorizeColumns } from './helpers/MandatorizeColumns';
import { stumbleOnMissingMandatoryColumn } from './helpers/stumbleOnMissingMandatoryColumn';

/**
 * In some custom table queries we assume that some columns
 * have always filled data. To ease type checking,
 * this function can be used to validate that those columns
 * contain neither null nor undefined and narrow the result type.
 *
 * This variant is useful for direct API calls.
 *
 * @see useTableQueryWithMandatoryColumns as a variant for (wrapped) useQuery.
 * @example
 * return validateMandatoryColumns(
 *   ['hash', 'hostname', 'lastChangeAt'],
 *   await postTablesManagementConfiguration({
 *     columns: ['hash', 'hostname', 'lastChangeAt'],
 *     filters: { sn: ['eq', sn] },
 *   }),
 * );
 */
export const validateMandatoryColumns = <
  TData extends { [key: string]: unknown },
  TMandatoryColumn extends keyof TData & string,
>(
  mandatoryColumns: TMandatoryColumn[],
  tableResponse: TableResponseBody<TData>,
): TableResponseBody<MandatorizeColumns<TData, TMandatoryColumn>> => {
  stumbleOnMissingMandatoryColumn(tableResponse.data, mandatoryColumns);

  // type compatibility is checked by the call above
  return tableResponse as any;
};
